import { useTranslation } from 'react-i18next';
import { CheckCircle2 } from 'lucide-react';

import {
  nextProductionWeekOption,
  type ProductionWeekOption,
} from '@/utils/productionScope';

interface ProductionScopeEmptyStateProps {
  isoWeek: string;
  productionWeek: string;
  soExternalId?: string;
  weeks: ProductionWeekOption[];
  onGoToNextWeek: (week: string) => void;
  onClear: () => void;
}

export function ProductionScopeEmptyState({
  isoWeek,
  productionWeek,
  soExternalId,
  weeks,
  onGoToNextWeek,
  onClear,
}: ProductionScopeEmptyStateProps) {
  const { t } = useTranslation();
  const nextWeek = productionWeek ? nextProductionWeekOption(weeks, productionWeek) : undefined;
  const soOnly = Boolean(soExternalId) && !productionWeek;

  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 px-6 py-16 text-center">
      <CheckCircle2 className="h-12 w-12 text-emerald-600" aria-hidden />
      <h3 className="mt-4 text-lg font-semibold text-emerald-950">
        {soOnly
          ? t('supply.productionScope.emptyTitleSo', { so: soExternalId })
          : t('supply.productionScope.emptyTitle', { iso: isoWeek })}
      </h3>
      <p className="mt-2 max-w-lg text-sm text-emerald-900/80">
        {soOnly ? t('supply.productionScope.emptyBodySo') : t('supply.productionScope.emptyBody')}
      </p>
      <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
        {nextWeek ? (
          <button
            type="button"
            className="rounded-md bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-800"
            onClick={() => onGoToNextWeek(nextWeek.week)}
          >
            {t('supply.productionScope.goNextWeek')}
          </button>
        ) : null}
        <button
          type="button"
          className="rounded-md border border-emerald-400 bg-white px-4 py-2 text-sm font-medium text-emerald-900 hover:bg-emerald-100"
          onClick={onClear}
        >
          {t('supply.productionScope.backToAggregated')}
        </button>
      </div>
    </div>
  );
}
